import {BaseApi} from './BaseApi'
import type {PaymentTermsValue} from './paymentTerms.constants'

/**
 * Enum for fields available for grouping and sorting
 */
export enum InvoiceFieldKey {
  WeekWorked = 'week_worked',
  SiteOrRegion = 'site_or_region',
  Position = 'position',
  Department = 'department',
  FirstName = 'first_name',
  LastName = 'last_name',
}

/**
 * Human-readable labels for each field key
 */
export const InvoiceFieldLabels: Record<InvoiceFieldKey, string> = {
  [InvoiceFieldKey.WeekWorked]: 'Week Worked',
  [InvoiceFieldKey.SiteOrRegion]: 'Site',
  [InvoiceFieldKey.Position]: 'Position',
  [InvoiceFieldKey.Department]: 'Department',
  [InvoiceFieldKey.FirstName]: 'First Name',
  [InvoiceFieldKey.LastName]: 'Last Name',
}

/**
 * Sort direction
 */
export enum SortDirection {
  Asc = 'asc',
  Desc = 'desc',
}

/**
 * Entity type for billing preferences
 */
export enum BillingPreferencesEntityType {
  Company = 'Company',
  Department = 'Department',
}

/**
 * Group-by field with order priority
 */
export interface GroupByField {
  field: InvoiceFieldKey
  order: number
}

/**
 * Sort-by field with direction and order priority
 */
export interface SortByField {
  field: InvoiceFieldKey
  direction: SortDirection
  order: number
}

/**
 * Billing preferences response from the API
 */
export interface BillingPreferencesResponse {
  id: string
  companyId: string
  departmentId?: string
  entityType: BillingPreferencesEntityType
  groupInvoiceBy: GroupByField[]
  sortInvoiceBy: SortByField[]
  invoiceStyleId?: string
  deliveryEmails: string[]
  paymentTerms?: PaymentTermsValue
  invoiceDay?: string
  /** STFFLG-3883 — cap in dollars. Absent or null = no cap. */
  maxInvoiceAmount?: number | null
  /** Separate Invoice By: one invoice per distinct value ('department' | 'site_or_region'). Absent or null = one invoice per batch. */
  separateInvoiceBy?: InvoiceFieldKey | null
  /** Free-text address shown under "Bill To" instead of the profile address (company level only) */
  poBoxAddress?: string
  /** When true, new invoices and credit memos use poBoxAddress in "Bill To" */
  usePoBoxAddress?: boolean
  /** STFFLG-4278: when true, Create New Batch can mark a batch as invoice-only (company level only) */
  allowInvoiceOnlyBatches?: boolean
  createdDate?: string
  modifiedDate?: string
  createdBy?: string
  modifiedBy?: string
  /** Company-level saves only: how many unapproved (Pending/Sent) invoices the save updated */
  invoicesUpdated?: number
}

/**
 * What a company-level save would touch, read before saving so the user can be warned
 */
export interface BillingPreferencesImpactResponse {
  /** Unapproved (Pending/Sent) invoices a company-level save would update */
  unapprovedInvoiceCount: number
}

/**
 * Request to create or update billing preferences
 */
export interface SaveBillingPreferencesRequest {
  companyId: string
  departmentId?: string
  entityType: BillingPreferencesEntityType
  groupInvoiceBy?: GroupByField[]
  sortInvoiceBy?: SortByField[]
  invoiceStyleId?: string
  deliveryEmails?: string[]
  paymentTerms?: PaymentTermsValue
  invoiceDay?: string
  /**
   * STFFLG-3883 — cap in dollars. Send `null` to CLEAR a stored cap; omitting the field leaves the
   * existing value untouched, which is not the same thing.
   */
  maxInvoiceAmount?: number | null
  /** Separate Invoice By. Same null/undefined contract as the cap: null clears, absent leaves alone. */
  separateInvoiceBy?: InvoiceFieldKey | null
  poBoxAddress?: string
  usePoBoxAddress?: boolean
  allowInvoiceOnlyBatches?: boolean
}

/**
 * BillingPreferencesApi - API service for billing preferences CRUD
 */
export class BillingPreferencesApi extends BaseApi {
  /**
   * Get billing preferences for a company/department
   */
  async getBillingPreferences(
    companyId: string,
    departmentId?: string,
  ): Promise<BillingPreferencesResponse | null> {
    const params: Record<string, string> = {companyId}
    if (departmentId) params.departmentId = departmentId

    const response = await this.client.get('/payroll/billing-preferences', {params})
    return response.data
  }

  /**
   * Get effective (resolved with inheritance) billing preferences
   */
  async getEffectivePreferences(
    companyId: string,
    departmentId?: string,
  ): Promise<BillingPreferencesResponse> {
    const params: Record<string, string> = {companyId}
    if (departmentId) params.departmentId = departmentId

    const response = await this.client.get('/payroll/billing-preferences/effective', {params})
    return response.data
  }

  /**
   * How many unapproved invoices saving the company billing preferences would update
   */
  async getBillingPreferencesImpact(companyId: string): Promise<BillingPreferencesImpactResponse> {
    const response = await this.client.get('/payroll/billing-preferences/impact', {
      params: {companyId},
    })
    return response.data
  }

  /**
   * Save billing preferences (create or update via upsert)
   */
  async saveBillingPreferences(
    data: SaveBillingPreferencesRequest,
  ): Promise<BillingPreferencesResponse> {
    const response = await this.client.post('/payroll/billing-preferences/upsert', data)
    return response.data
  }

  /**
   * Update existing billing preferences by ID
   */
  async updateBillingPreferences(
    id: string,
    data: Partial<SaveBillingPreferencesRequest>,
  ): Promise<BillingPreferencesResponse> {
    const response = await this.client.put(`/payroll/billing-preferences/${id}`, data)
    return response.data
  }

  /**
   * Delete billing preferences (reset to defaults)
   */
  async resetBillingPreferences(id: string): Promise<void> {
    await this.client.delete(`/payroll/billing-preferences/${id}`)
  }

  /**
   * Get invoice-level billing preferences
   */
  async getInvoiceBillingPreferences(
    invoiceId: string,
  ): Promise<BillingPreferencesResponse | null> {
    const response = await this.client.get(`/payroll/invoices/${invoiceId}/billing-preferences`)
    return response.data
  }

  /**
   * Update invoice-level billing preferences.
   * Pass { refresh: true } to reset the invoice to the company defaults.
   */
  async updateInvoiceBillingPreferences(
    invoiceId: string,
    data: {
      refresh?: boolean
      groupInvoiceBy?: GroupByField[]
      sortInvoiceBy?: SortByField[]
      invoiceStyleId?: string
      deliveryEmails?: string[]
      paymentTerms?: PaymentTermsValue
      invoiceDay?: string
    },
  ): Promise<BillingPreferencesResponse> {
    const response = await this.client.put(
      `/payroll/invoices/${invoiceId}/billing-preferences`,
      data,
    )
    return response.data
  }
}

// Singleton instance
export const billingPreferencesApi = new BillingPreferencesApi()
